/**
 * ArrayJoiner - Utility for joining arrays into strings
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for joining arrays into comma-separated strings,
 * which is useful for logging, error messages, and display purposes.
 */

/**
 * Join array elements into a comma-separated string
 * 
 * Converts an array to a comma-separated string. Returns empty string for empty arrays.
 * 
 * @param array - Array to join
 * @param separator - Separator string (default: ', ')
 * @returns Joined string, or empty string if array is empty
 */
export function joinArray<T>(array: T[], separator: string = ', '): string {
  if (array.length === 0) {
    return '';
  }
  
  return array.join(separator);
}

/**
 * Join array elements with a fallback value
 * 
 * Joins array elements, or returns a fallback value if the array is empty.
 * 
 * @param array - Array to join
 * @param fallback - Value to return if array is empty (default: 'none')
 * @param separator - Separator string (default: ', ')
 * @returns Joined string, or fallback if array is empty
 */
export function joinArrayWithFallback<T>(
  array: T[],
  fallback: string = 'none',
  separator: string = ', '
): string {
  if (array.length === 0) {
    return fallback;
  }
  
  return array.join(separator);
}

/**
 * Join array elements with optional separator
 * 
 * Joins array elements with the specified separator.
 * 
 * @param array - Array to join
 * @param separator - Separator string
 * @returns Joined string
 */
export function joinArrayWithSeparator<T>(array: T[], separator: string): string {
  return array.join(separator);
}

